import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DownloadSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { hoyISO, fmtHora } from "@/lib/datetime";
import { useEmpresa } from "@/contexts/EmpresaContext";
import { useAuth, canManage } from "@/contexts/AuthContext";
import { PermisosTab, SuspensionesTab, ExtrasTab, SuplenciasTab } from "@/components/IncidenciasEscuela";

// Asistencia por clase y nómina por hora-clase (solo escuelas). El backend calcula
// todo al vuelo contra la carga docente (asistencia_docente.py); aquí solo se muestra.

const ESTATUS = {
  impartida: { label: "Impartida", cls: "bg-emerald-100 text-emerald-800" },
  retardo: { label: "Retardo", cls: "bg-amber-100 text-amber-800" },
  salida_anticipada: { label: "Salida anticipada", cls: "bg-amber-100 text-amber-800" },
  falta: { label: "Falta", cls: "bg-red-100 text-red-800" },
  sin_entrada: { label: "Sin entrada", cls: "bg-orange-100 text-orange-800" },
  sin_salida: { label: "Sin salida", cls: "bg-orange-100 text-orange-800" },
  justificada: { label: "Justificada", cls: "bg-blue-100 text-blue-800" },
  pendiente: { label: "Pendiente", cls: "bg-slate-100 text-slate-600" },
  inhabil: { label: "Día inhábil", cls: "bg-violet-100 text-violet-800" },
  suspendida: { label: "Suspendida", cls: "bg-violet-100 text-violet-800" },
};

const dinero = (n) => (n == null ? "—" : n.toLocaleString("es-MX", { style: "currency", currency: "MXN" }));

function inicioQuincena() {
  const hoy = hoyISO();
  return `${hoy.slice(0, 8)}${Number(hoy.slice(8)) <= 15 ? "01" : "16"}`;
}

function Chip({ estatus }) {
  const e = ESTATUS[estatus] || { label: estatus, cls: "bg-slate-100" };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${e.cls}`}>{e.label}</span>;
}

function Detalle({ empleadoId, desde, hasta, tzCentro, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!empleadoId) return;
    setData(null);
    api.get(`/escuela/asistencia/${empleadoId}`, { params: { fecha_desde: desde, fecha_hasta: hasta } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [empleadoId, desde, hasta]);

  const n = data?.nombres || { materias: {}, grupos: {}, centros: {}, docentes: {} };
  const hora = (m, centroId) => (m ? fmtHora(m.hora, tzCentro(centroId)) : "—");

  return (
    <Dialog open={!!empleadoId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="asistencia-detalle">
        <DialogHeader>
          <DialogTitle className="font-display">{data?.nombre || "Cargando…"}</DialogTitle>
        </DialogHeader>
        {data && (
          <>
            <div className="text-sm text-slate-600">
              {data.pagables} de {data.programadas} horas-clase a pagar · {data.faltas} falta(s) · {data.por_revisar} por revisar
              {data.importe != null && <> · <strong>{dinero(data.importe)}</strong></>}
            </div>
            {data.dias.length === 0 && <div className="text-sm text-slate-500 py-6">Sin clases ni marcas en el periodo.</div>}
            <div className="space-y-4 mt-2">
              {data.dias.map((d) => (
                <div key={d.fecha} className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 text-sm font-semibold text-ink">{d.dia_semana} {d.fecha}</div>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <tbody>
                        {d.clases.map((c) => (
                          <tr key={`${c.clase_id}-${c.origen}`}>
                            <td className="whitespace-nowrap font-mono text-xs">{c.hora_inicio}–{c.hora_fin}</td>
                            <td>
                              <div className="font-semibold">{c.tipo === "clase" ? n.materias[c.materia_id] || "—" : c.actividad || "Administrativo"}</div>
                              <div className="text-xs text-slate-500">{[n.grupos[c.grupo_id], n.centros[c.centro_id]].filter(Boolean).join(" · ")}</div>
                              {c.origen === "suplencia" && <div className="text-xs font-semibold text-brand-accent">Suplencia de {n.docentes[c.titular_id] || "otro docente"}</div>}
                              {c.origen === "extra" && <div className="text-xs font-semibold text-brand-accent">{c.actividad}</div>}
                              {c.cubierta_por && <div className="text-xs text-slate-600">Cubierta por {n.docentes[c.cubierta_por] || "suplente"}</div>}
                            </td>
                            <td><Chip estatus={c.estatus} /></td>
                            <td className="text-xs whitespace-nowrap">
                              Entrada {hora(c.entrada, c.centro_id)} · Salida {hora(c.salida, c.centro_id)}
                            </td>
                            <td className="text-xs text-slate-500">
                              {c.minutos_retardo > 0 && `${c.minutos_retardo} min tarde. `}
                              {c.minutos_salida_anticipada > 0 && `Salió ${c.minutos_salida_anticipada} min antes. `}
                              {c.nota}
                            </td>
                          </tr>
                        ))}
                        {d.fuera_de_carga.map((m, i) => (
                          <tr key={`f${i}`} className="text-slate-500">
                            <td className="font-mono text-xs">{hora(m, m.centro_id)}</td>
                            <td colSpan={4} className="text-xs">Marca de {m.tipo} fuera de carga (no se paga)</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReporteAcademico({ desde, hasta, recargar }) {
  const [agrupar, setAgrupar] = useState("materia");
  const [filas, setFilas] = useState([]);

  useEffect(() => {
    api.get("/escuela/reporte-academico", { params: { fecha_desde: desde, fecha_hasta: hasta, agrupar } })
      .then((r) => setFilas(r.data.filas))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [desde, hasta, agrupar, recargar]);

  const color = (pct) => (pct == null ? "" : pct >= 95 ? "text-emerald-700" : pct >= 85 ? "text-amber-700" : "text-red-700");

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-600">Agrupar por</span>
        {[["materia", "Materia"], ["grupo", "Grupo"], ["centro", "Plantel"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={agrupar === v ? "default" : "outline"} className={agrupar === v ? "bg-brand hover:bg-brand-hover" : ""}
            onClick={() => setAgrupar(v)} data-testid={`academico-${v}`}>{l}</Button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="data-table" data-testid="academico-tabla">
          <thead>
            <tr><th>{{ materia: "Materia", grupo: "Grupo", centro: "Plantel" }[agrupar]}</th><th>Programadas</th><th>Dadas</th><th>Cubiertas por suplente</th>
              <th>Faltas</th><th>Justificadas</th><th>Por revisar</th><th>Suspendidas</th><th>Cumplimiento</th><th>Docentes con faltas</th></tr>
          </thead>
          <tbody>
            {filas.length === 0 && <tr><td colSpan={10} className="text-center text-slate-500 py-8">Sin clases en el periodo</td></tr>}
            {filas.map((f) => (
              <tr key={f.id || "sin"}>
                <td className="font-semibold">{f.nombre}</td>
                <td>{f.programadas}</td>
                <td>{f.impartidas}</td>
                <td>{f.cubiertas || ""}</td>
                <td className={f.faltas ? "text-red-700 font-semibold" : ""}>{f.faltas || ""}</td>
                <td>{f.justificadas || ""}</td>
                <td>{f.por_revisar || ""}</td>
                <td>{f.suspendidas || ""}</td>
                <td className={`font-bold ${color(f.cumplimiento_pct)}`}>{f.cumplimiento_pct == null ? "—" : `${f.cumplimiento_pct}%`}</td>
                <td className="text-xs text-slate-600">{f.docentes_con_faltas.map((d) => `${d.nombre} (${d.faltas})`).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Cumplimiento = clases dadas o cubiertas por suplente entre las programadas que ya pasaron. Las suspendidas no cuentan.</p>
    </div>
  );
}

const TOUR_STORAGE_KEY = "chkr_tour_asistencia_docente_seen";

const TOUR_STEPS = [
  { target: "[data-tour='asd-periodo']", title: "Periodo", content: "Elige las fechas (por omisión, la quincena en curso) y da clic en Calcular. La asistencia se calcula comparando las marcas del checador y del portal contra la carga docente de esos días." },
  { target: "[data-tour='asd-exportar']", title: "Exportar a Excel", content: "Descarga la nómina del periodo con el detalle clase por clase, para pagar o para revisarla fuera de CHKR." },
  { target: "[data-tour='asd-tabs']", title: "Nómina", content: "Por docente: horas-clase programadas, impartidas, faltas, justificadas, por revisar y suplencias, con el importe si hay tarifa. Da clic en un docente para ver cada clase. \"Por revisar\" quiere decir que le faltó checar la entrada o la salida de un bloque: no se paga hasta que RH lo resuelva." },
  { target: "[data-tour='asd-tab-suplencias']", title: "Suplencias", content: "Cuando un titular falta y otro docente libre checa en el plantel a esa hora, la clase se le acredita al suplente. Si hay varios candidatos, aquí RH elige quién la cubrió." },
  { target: "[data-tour='asd-tab-permisos']", title: "Permisos por clase", content: "Permisos para solo algunas clases de un día, con o sin goce. Para un día completo usa Incidencias, como con el resto del personal." },
  { target: "[data-tour='asd-tab-suspensiones']", title: "Suspensiones", content: "Días sin clases por plantel, turno o grupo (junta, evento, contingencia). Las clases suspendidas no cuentan como falta; al crearla eliges si se pagan." },
  { target: "[data-tour='asd-tab-extras']", title: "Clases extra", content: "Reposiciones o clases fuera de la carga que la escuela autoriza y paga. El docente tiene que checarlas como cualquier otra clase." },
  { target: "[data-tour='asd-tab-academico']", title: "Por materia / grupo", content: "El mismo periodo visto por materia, grupo o plantel: qué clases se dieron y cuáles no. Pensado para coordinación académica." },
];

export default function AsistenciaDocente() {
  const { empresaActivaId } = useEmpresa();
  const { user } = useAuth();
  const editable = canManage(user);
  const [catalogo, setCatalogo] = useState({ docentes: [], materias: [], grupos: [], modulos: [] });
  const [version, setVersion] = useState(0); // sube cuando cambia una incidencia: recalcula nómina y suplencias
  const [desde, setDesde] = useState(inicioQuincena());
  const [hasta, setHasta] = useState(hoyISO());
  const [data, setData] = useState(null);
  const [centros, setCentros] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const cargar = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/escuela/nomina", { params: { fecha_desde: desde, fecha_hasta: hasta } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    cargar();
    Promise.all([
      api.get("/centros"), api.get("/empleados"), api.get("/escuela/materias"), api.get("/escuela/grupos"), api.get("/escuela/modulos"),
    ]).then(([c, e, m, g, mo]) => {
      setCentros(c.data);
      setCatalogo({ docentes: e.data.filter((x) => x.tipo_personal === "docente"), materias: m.data, grupos: g.data, modulos: mo.data });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActivaId]);

  const huboCambio = () => {
    setVersion((v) => v + 1);
    cargar();
  };

  const cat = {
    ...catalogo,
    centros: centros.filter((c) => c.activo !== false),
    docente: (id) => catalogo.docentes.find((d) => d.id === id)?.nombre || "—",
    materia: (id) => catalogo.materias.find((m) => m.id === id)?.nombre || "—",
    grupo: (id) => catalogo.grupos.find((g) => g.id === id)?.nombre || "—",
    centro: (id) => centros.find((c) => c.id === id)?.nombre || "—",
    turnos: (centroId) => [...new Set(catalogo.modulos.filter((m) => m.centro_id === centroId).map((m) => m.turno))],
  };

  const tzCentro = (id) => centros.find((c) => c.id === id)?.zona_horaria;

  const exportar = async () => {
    try {
      const { data: blob } = await api.get("/escuela/nomina/export.xlsx", {
        params: { fecha_desde: desde, fecha_hasta: hasta }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nomina_hora_clase_${desde}_${hasta}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  const filas = data?.docentes || [];
  const suma = (k) => filas.reduce((t, f) => t + (f[k] || 0), 0);
  const hayImporte = filas.some((f) => f.importe != null);

  return (
    <div className="p-6 md:p-10" data-testid="asistencia-docente-page">
      <PageHeader
        kicker="Escuela"
        title="Asistencia docente"
        subtitle={`Horas-clase impartidas y a pagar por docente. Retardo de hasta ${data?.config?.minutos_retardo_clase ?? "N"} min se paga; después cuenta como falta.`}
        actions={<TourHelpButton onClick={startTour} />}
      />
      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <div className="flex flex-wrap gap-3 items-end mb-4" data-tour="asd-periodo">
        <div className="space-y-1">
          <Label className="label-uppercase">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-44" data-testid="nomina-desde" />
        </div>
        <div className="space-y-1">
          <Label className="label-uppercase">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-44" data-testid="nomina-hasta" />
        </div>
        <Button onClick={cargar} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="nomina-calcular">
          <ArrowsClockwise size={16} className="mr-2" /> {busy ? "Calculando…" : "Calcular"}
        </Button>
        <Button variant="outline" onClick={exportar} data-testid="nomina-exportar" data-tour="asd-exportar">
          <DownloadSimple size={16} className="mr-2" /> Exportar Excel
        </Button>
      </div>

      <Tabs defaultValue="nomina">
        <TabsList className="mb-4 flex-wrap h-auto" data-tour="asd-tabs">
          <TabsTrigger value="nomina">Nómina</TabsTrigger>
          <TabsTrigger value="suplencias" data-testid="tab-suplencias" data-tour="asd-tab-suplencias">Suplencias</TabsTrigger>
          <TabsTrigger value="permisos" data-testid="tab-permisos" data-tour="asd-tab-permisos">Permisos por clase</TabsTrigger>
          <TabsTrigger value="suspensiones" data-testid="tab-suspensiones" data-tour="asd-tab-suspensiones">Suspensiones</TabsTrigger>
          <TabsTrigger value="extras" data-testid="tab-extras" data-tour="asd-tab-extras">Clases extra</TabsTrigger>
          <TabsTrigger value="academico" data-testid="tab-academico" data-tour="asd-tab-academico">Por materia / grupo</TabsTrigger>
        </TabsList>
        <TabsContent value="nomina">
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="nomina-tabla">
            <thead>
              <tr>
                <th>Docente</th>
                <th>Programadas</th>
                <th>Impartidas</th>
                <th>Retardos</th>
                <th>Faltas</th>
                <th>Por revisar</th>
                <th>Justificadas</th>
                <th>Pendientes</th>
                <th>Suspendidas</th>
                <th>Suplencias</th>
                <th>Extras</th>
                <th>A pagar</th>
                {hayImporte && <th>Tarifa</th>}
                {hayImporte && <th>Importe</th>}
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={14} className="text-center text-slate-500 py-8">{busy ? "Calculando…" : "Sin docentes con carga en el periodo"}</td></tr>
              )}
              {filas.map((f) => (
                <tr key={f.empleado_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetalle(f.empleado_id)} data-testid={`nomina-fila-${f.empleado_id}`}>
                  <td>
                    <div className="font-semibold text-ink">{f.nombre}</div>
                    <div className="text-xs text-slate-500 font-mono">{f.numero_empleado}</div>
                  </td>
                  <td>{f.programadas}</td>
                  <td>{f.impartidas}</td>
                  <td>{f.retardos || ""}</td>
                  <td className={f.faltas ? "text-red-700 font-semibold" : ""}>{f.faltas || ""}</td>
                  <td className={f.por_revisar ? "text-orange-700 font-semibold" : ""}>{f.por_revisar || ""}</td>
                  <td>{f.justificadas_pagadas + f.justificadas_sin_pago || ""}</td>
                  <td className="text-slate-500">{f.pendientes || ""}</td>
                  <td>{f.suspendidas || ""}</td>
                  <td>{f.suplencias || ""}{f.cubiertas_por_suplente ? <span className="text-xs text-slate-500"> ({f.cubiertas_por_suplente} cubiertas)</span> : ""}</td>
                  <td>{f.extras || ""}</td>
                  <td className="font-semibold">{f.pagables}</td>
                  {hayImporte && <td>{dinero(f.tarifa_hora_clase)}</td>}
                  {hayImporte && <td className="font-semibold">{dinero(f.importe)}</td>}
                </tr>
              ))}
              {filas.length > 1 && (
                <tr className="bg-slate-50 font-semibold">
                  <td>Total</td>
                  <td>{suma("programadas")}</td>
                  <td>{suma("impartidas")}</td>
                  <td>{suma("retardos")}</td>
                  <td>{suma("faltas")}</td>
                  <td>{suma("por_revisar")}</td>
                  <td>{suma("justificadas_pagadas") + suma("justificadas_sin_pago")}</td>
                  <td>{suma("pendientes")}</td>
                  <td>{suma("suspendidas")}</td>
                  <td>{suma("suplencias")}</td>
                  <td>{suma("extras")}</td>
                  <td>{suma("pagables")}</td>
                  {hayImporte && <td></td>}
                  {hayImporte && <td>{dinero(suma("importe"))}</td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-3">
        "Por revisar": tramos con entrada o salida sin marcar; no se pagan hasta justificarlos. Haz clic en un docente para ver el detalle clase por clase.
      </p>
        </TabsContent>
        <TabsContent value="suplencias"><SuplenciasTab desde={desde} hasta={hasta} cat={cat} editable={editable} recargar={version} onCambio={huboCambio} /></TabsContent>
        <TabsContent value="permisos"><PermisosTab desde={desde} hasta={hasta} cat={cat} editable={editable} onCambio={huboCambio} /></TabsContent>
        <TabsContent value="suspensiones"><SuspensionesTab desde={desde} hasta={hasta} cat={cat} editable={editable} onCambio={huboCambio} /></TabsContent>
        <TabsContent value="extras"><ExtrasTab desde={desde} hasta={hasta} cat={cat} editable={editable} onCambio={huboCambio} /></TabsContent>
        <TabsContent value="academico"><ReporteAcademico desde={desde} hasta={hasta} recargar={version} /></TabsContent>
      </Tabs>

      <Detalle empleadoId={detalle} desde={desde} hasta={hasta} tzCentro={tzCentro} onClose={() => setDetalle(null)} />
    </div>
  );
}
